/**
 * Health landing layout — matches site MainFrame / maxWidthScreen (1072px).
 * Column splits only at lg (1024px). Do not use sm:/md: for layout (tailwind sm = 100px).
 */
import { maxWidthScreen } from "../GlobalVar";

export const HEALTH_MAX_WIDTH = maxWidthScreen;

/** Aimma Doctor hero — 460px band; overflow clips enlarged screenshot at bottom edge */
export const healthAimmaDoctorHeroSurface =
    "relative isolate w-full bg-white lg:h-[460px] lg:overflow-hidden";

/** Aimma Patient hero — backdrop band on mobile; fixed band on desktop */
export const healthAimmaPatientHeroSurface =
    "relative isolate w-full overflow-hidden bg-white lg:h-[460px] lg:min-h-0";

/** Patient hero — tighter lower band reserve pulls image+mask closer to CTAs */
export const healthChildPageHeroBackdropSectionY =
    "pt-20 pb-44 sm:pb-52 lg:pt-24 lg:pb-0";

/** Hero foreground copy — above bottom fade (fade may cover right image) */
export const healthAimmaDoctorHeroCopyForeground = "relative z-[2]";

/** Outer section: full bleed */
/** overflow-x-clip — not overflow-x-hidden (which forces overflow-y: auto per CSS spec). */
/** Landing hero uses healthHeroSurface overflow-hidden for vertical clip; child heroes use band + bottom fade. */
export const healthSectionOuter =
    "w-full overflow-x-clip text-left [text-align:left]";

/** Centered content column — same as MainFrame inner wrapper */
export const healthSectionInnerStyle = { maxWidth: HEALTH_MAX_WIDTH } as const;

/** Horizontal padding aligned with MainFrame (px-8 mobile, flush at lg) */
export const healthContentPadX = "w-full px-8 lg:px-0";

export const healthSectionY = "py-16 lg:py-28";
export const healthSectionYCompact = "py-16 lg:py-24";

export const healthSectionBlock =
    "flex w-full min-w-0 flex-col gap-8 lg:gap-10 text-left";

/** Hero vertical rhythm — mobile/tablet only; desktop uses h-screen centering */
export const healthHeroSectionY = "pt-20 pb-12 lg:pt-0 lg:pb-0";

/**
 * Child module hero — copy + image top-aligned below navbar; image extends down, clipped at band bottom.
 */
export const healthChildPageHeroSectionY = "pt-20 pb-8 lg:pt-24 lg:pb-0";

/** Copy-first two-column child hero — product line full width; image row aligns with H1 */
export const healthChildPageHeroGrid =
    "grid w-full min-w-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-x-10 lg:gap-y-6 lg:items-start";

export const healthChildPageHeroProductLineRow = "lg:col-span-2";

/** Left hero copy block (H1 downward) */
export const healthChildPageHeroCopyStack =
    "flex w-full min-w-0 flex-col gap-5 lg:gap-6";

/** Right hero image — below bottom fade so gradient can soften screenshot edge */
export const healthChildPageHeroImageColumn = "relative z-0 w-full min-w-0";

/**
 * Patient hero (heroBackdropRight) — copy-only column; lifestyle image is absolute on the right.
 * Other child pages keep `healthChildPageHeroGrid` + compact product preview.
 */
export const healthChildPageHeroBackdropGrid =
    "relative z-[2] grid w-full min-w-0 grid-cols-1 gap-6 lg:max-w-[min(100%,36rem)] lg:gap-y-5";

/** Child product sections — tighter than healthSectionYCompact */
export const healthChildSectionY = "py-12 lg:py-16";

/** Aimma Doctor layout polish — denser section rhythm (page-scoped via HealthSection) */
export const healthChildSectionYDense = "py-10 lg:py-14";

export const healthChildSectionYTight = "py-10 lg:py-14";

export const healthAimmaDoctorSectionContentOffset = "mt-5 lg:mt-6";

/**
 * Desktop hero — `h-screen` band, navbar clearance (`pt-24`), bottom inset (`pb-16`)
 * so flex center sits slightly above true viewport midpoint (matches pre–hero-fix landing).
 */
export const healthHeroDesktopLayout =
    "lg:box-border lg:h-screen lg:max-h-screen lg:flex lg:flex-col lg:justify-center lg:pt-24 lg:pb-16";

/** Hero: stack below lg; copy ~45% / visual ~55% at lg+ (reference proportions) */
export const healthHeroGrid =
    "grid w-full min-w-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-12 lg:items-center";

/** Standard two-column — stack below lg */
export const healthSplitLg =
    "grid w-full min-w-0 grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12 lg:items-center";

export const healthSplitLgStart =
    "grid w-full min-w-0 grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12 lg:items-start";

export const healthCol = "w-full min-w-0";

export const healthColStack =
    "flex w-full min-w-0 flex-col gap-5 lg:gap-6";

export const healthGrid2Lg =
    "grid w-full min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8";

export const healthFullWidthPanel = "w-full min-w-0";

/** Hero visual column — visible overflow; section-level overflow-x-clip contains page bleed */
export const healthHeroLoopWrap =
    "relative mx-auto flex w-full min-w-0 max-w-full shrink-0 justify-center overflow-visible px-1 sm:px-1.5 lg:justify-end lg:px-0";
